import React, { useEffect, useMemo, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { Table } from "react-super-responsive-table";
import { Loader2, Printer, SlidersHorizontal, X } from "lucide-react";
import "react-super-responsive-table/dist/SuperResponsiveTableStyle.css";
import "../styles/TableStyles.css";

const TEXT_FILTERS_DEFAULT = {
  search: "",
  skupinaVp: "",
};

const RANGE_FILTERS_DEFAULT = {
  mnozstvi: { mode: "", value1: "", value2: "" },
  ncsdKs: { mode: "", value1: "", value2: "" },
  ncsdSum: { mode: "", value1: "", value2: "" },
  exspirace: { mode: "", value1: "", value2: "" },
  zbyvaDni: { mode: "", value1: "", value2: "" },
};

const SKUPINA_VP_OPTIONS = ["1", "A", "B", "C", "D", "X"];

function parseCzDateToDate(value) {
  if (!value) return null;

  if (value instanceof Date && !isNaN(value.getTime())) return value;

  const str = String(value).trim();

  const match = str.match(/^(\d{1,2})\.\s*(\d{1,2})\.\s*(\d{4})$/);
  if (match) {
    const day = Number(match[1]);
    const month = Number(match[2]);
    const year = Number(match[3]);
    const d = new Date(year, month - 1, day);
    return isNaN(d.getTime()) ? null : d;
  }

  const fallback = new Date(str);
  return isNaN(fallback.getTime()) ? null : fallback;
}

function normalizeNumber(value) {
  if (value === null || value === undefined || value === "") return null;

  if (typeof value === "number") {
    return Number.isFinite(value) ? value : null;
  }

  const cleaned = String(value)
    .replace(/\u00A0/g, "")
    .replace(/\s+/g, "")
    .replace(",", ".")
    .replace(/[^\d.-]/g, "");

  if (!cleaned) return null;

  const num = Number(cleaned);
  return Number.isNaN(num) ? null : num;
}

function compareByMode(itemValue, mode, value1, value2) {
  if (!mode) return true;
  if (itemValue === null || itemValue === undefined) return false;

  if (mode === "gt") return itemValue > value1;
  if (mode === "gte") return itemValue >= value1;
  if (mode === "lt") return itemValue < value1;
  if (mode === "lte") return itemValue <= value1;
  if (mode === "between") {
    if (value1 === null || value2 === null) return true;
    const min = Math.min(value1, value2);
    const max = Math.max(value1, value2);
    return itemValue >= min && itemValue <= max;
  }

  return true;
}

function formatMoney(value) {
  const num = normalizeNumber(value);
  if (num === null) return "";

  return num.toLocaleString("cs-CZ", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function formatPlainNumber(value) {
  const num = normalizeNumber(value);
  if (num === null) return value ?? "";

  return num.toLocaleString("cs-CZ", {
    maximumFractionDigits: 2,
  });
}

function formatCzDateCompact(value) {
  const d = parseCzDateToDate(value);
  if (!d || isNaN(d.getTime())) return "";

  return `${d.getDate()}.${d.getMonth() + 1}.${d.getFullYear()}`;
}

export default function LezakyPage() {
  const { user } = useAuth();

  const [data, setData] = useState([]);
  const [textFilters, setTextFilters] = useState(TEXT_FILTERS_DEFAULT);
  const [rangeFilters, setRangeFilters] = useState(RANGE_FILTERS_DEFAULT);
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [sortField, setSortField] = useState("ncsdSum");
  const [sortDirection, setSortDirection] = useState("desc");
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);

  useEffect(() => {
    if (!user || !user.pharmacyCode) {
      setLoading(false);
      return;
    }

    const firma = user.firma || "LEMON";
    const pc = String(user.pharmacyCode).replace(/^0+/, "");

    const fetchData = async () => {
      try {
        const response = await fetch(`/api/lezaky/${firma}/${pc}`);
        if (!response.ok) throw new Error("Chyba při načítání ležáků");

        const json = await response.json();
        setLastUpdated(json.lastBatch || null);

        const today = new Date();

        const active = (Array.isArray(json.lezaky) ? json.lezaky : []).filter(
          (x) => !x.stav || !["uzavřeno", "odesláno", "přijato"].includes(x.stav)
        );

        const normalized = active.map((x) => {
          const exspiraceRaw = x.exspirace ?? "";
          const exspiraceDate = parseCzDateToDate(exspiraceRaw);
          const exspirace = exspiraceDate ? formatCzDateCompact(exspiraceDate) : "";

          let zbyvaDni = x.zbyvaDni ?? null;

          if (zbyvaDni == null && exspiraceDate) {
            zbyvaDni = Math.ceil((exspiraceDate - today) / (1000 * 60 * 60 * 24));
          }

          return {
            kodVZP: x.kodVZP ?? "",
            pdk: x.pdk ?? "",
            nazev: x.nazev ?? "",
            skupinaVp: String(x.skupinaVp ?? "").trim().toUpperCase(),
            mnozstvi: x.mnozstvi ?? "",
            ncsdKs: normalizeNumber(x.ncsdKs),
            ncsdSum: normalizeNumber(x.ncsdSum),
            exspirace,
            zbyvaDni,
          };
        });

        setData(normalized);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [user]);

  const applyNumberRangeFilter = (items, key) => {
    const filter = rangeFilters[key];
    if (!filter.mode) return items;

    const value1 = normalizeNumber(filter.value1);
    const value2 = normalizeNumber(filter.value2);

    if (filter.mode === "between" && (value1 === null || value2 === null)) return items;
    if (filter.mode !== "between" && value1 === null) return items;

    return items.filter((item) => {
      const itemValue = normalizeNumber(item[key]);
      return compareByMode(itemValue, filter.mode, value1, value2);
    });
  };

  const applyDateRangeFilter = (items, key) => {
    const filter = rangeFilters[key];
    if (!filter.mode) return items;

    const date1 = filter.value1 ? new Date(filter.value1) : null;
    const date2 = filter.value2 ? new Date(filter.value2) : null;

    const value1 =
      date1 && !isNaN(date1.getTime())
        ? new Date(date1.getFullYear(), date1.getMonth(), date1.getDate()).getTime()
        : null;

    const value2 =
      date2 && !isNaN(date2.getTime())
        ? new Date(date2.getFullYear(), date2.getMonth(), date2.getDate()).getTime()
        : null;

    if (filter.mode === "between" && (value1 === null || value2 === null)) return items;
    if (filter.mode !== "between" && value1 === null) return items;

    return items.filter((item) => {
      const itemDate = parseCzDateToDate(item[key]);
      if (!itemDate) return false;

      const itemValue = new Date(
        itemDate.getFullYear(),
        itemDate.getMonth(),
        itemDate.getDate()
      ).getTime();

      return compareByMode(itemValue, filter.mode, value1, value2);
    });
  };

  const filteredAndSortedData = useMemo(() => {
    let result = [...data];

    result = result.filter((item) => {
      const search = textFilters.search.trim().toLowerCase();

      const searchMatch =
        !search ||
        String(item.kodVZP || "").toLowerCase().includes(search) ||
        String(item.pdk || "").toLowerCase().includes(search) ||
        String(item.nazev || "").toLowerCase().includes(search);

      const selectedVp = textFilters.skupinaVp;

      let skupinaVpMatch = true;
      if (selectedVp === "__empty__") {
        skupinaVpMatch = !String(item.skupinaVp || "").trim();
      } else if (selectedVp) {
        skupinaVpMatch = String(item.skupinaVp || "").trim().toUpperCase() === selectedVp;
      }

      return searchMatch && skupinaVpMatch;
    });

    result = applyNumberRangeFilter(result, "mnozstvi");
    result = applyNumberRangeFilter(result, "ncsdKs");
    result = applyNumberRangeFilter(result, "ncsdSum");
    result = applyDateRangeFilter(result, "exspirace");
    result = applyNumberRangeFilter(result, "zbyvaDni");

    result.sort((a, b) => {
      const aVal = a?.[sortField];
      const bVal = b?.[sortField];

      if (["mnozstvi", "zbyvaDni", "ncsdKs", "ncsdSum"].includes(sortField)) {
        const aNum = normalizeNumber(aVal) ?? 0;
        const bNum = normalizeNumber(bVal) ?? 0;
        return sortDirection === "asc" ? aNum - bNum : bNum - aNum;
      }

      if (sortField === "exspirace") {
        const aDate = parseCzDateToDate(aVal)?.getTime() ?? 0;
        const bDate = parseCzDateToDate(bVal)?.getTime() ?? 0;
        return sortDirection === "asc" ? aDate - bDate : bDate - aDate;
      }

      const aStr = String(aVal ?? "").toLowerCase();
      const bStr = String(bVal ?? "").toLowerCase();

      return sortDirection === "asc"
        ? aStr.localeCompare(bStr, "cs")
        : bStr.localeCompare(aStr, "cs");
    });

    return result;
  }, [data, textFilters, rangeFilters, sortField, sortDirection]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
      return;
    }

    setSortField(field);
    setSortDirection(field === "ncsdSum" ? "desc" : "asc");
  };

  const handleTextFilterChange = (key, value) => {
    setTextFilters((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleRangeFilterChange = (key, field, value) => {
    setRangeFilters((prev) => ({
      ...prev,
      [key]: {
        ...prev[key],
        [field]: value,
      },
    }));
  };

  const clearFilters = () => {
    setTextFilters(TEXT_FILTERS_DEFAULT);
    setRangeFilters(RANGE_FILTERS_DEFAULT);
    setSortField("ncsdSum");
    setSortDirection("desc");
  };

  const handlePrint = () => {
    window.print();
  };

  const renderRangeFilter = (key, label, type = "number") => {
    const filter = rangeFilters[key];
    const isBetween = filter.mode === "between";

    return (
      <div className="border rounded-xl p-3 bg-white shadow-sm">
        <div className="text-xs font-semibold text-gray-600 mb-2">{label}</div>

        <select
          value={filter.mode}
          onChange={(e) => handleRangeFilterChange(key, "mode", e.target.value)}
          className="border p-2 rounded w-full mb-2"
        >
          <option value="">Bez filtru</option>
          <option value="gt">Větší než</option>
          <option value="gte">Větší nebo rovno</option>
          <option value="lt">Menší než</option>
          <option value="lte">Menší nebo rovno</option>
          <option value="between">Interval</option>
        </select>

        <div className="flex gap-2">
          <input
            type={type}
            value={filter.value1}
            onChange={(e) => handleRangeFilterChange(key, "value1", e.target.value)}
            placeholder={isBetween ? "Od" : "Hodnota"}
            className="border p-2 rounded w-full"
          />

          {isBetween && (
            <input
              type={type}
              value={filter.value2}
              onChange={(e) => handleRangeFilterChange(key, "value2", e.target.value)}
              placeholder="Do"
              className="border p-2 rounded w-full"
            />
          )}
        </div>
      </div>
    );
  };

  const renderSortMark = (field) => {
    if (sortField !== field) return "";
    return sortDirection === "asc" ? " ▲" : " ▼";
  };

  return (
    <div id="lezaky-print-root" className="p-4 print:p-0">
      <style>{`
        @media print {
          @page {
            size: A4 portrait;
            margin: 7mm;
          }

          body * {
            visibility: hidden !important;
          }

          #lezaky-print-root,
          #lezaky-print-root * {
            visibility: visible !important;
          }

          #lezaky-print-root {
            position: absolute !important;
            left: 0 !important;
            top: 0 !important;
            width: 100% !important;
            background: #fff !important;
            padding: 0 !important;
            margin: 0 !important;
          }

          .no-print {
            display: none !important;
          }

          .print-only {
            display: block !important;
          }

          .print-table-wrap {
            overflow: visible !important;
          }

          .custom-table {
            width: 100% !important;
            border-collapse: collapse !important;
            font-size: 8px !important;
            table-layout: fixed !important;
          }

          .custom-table th,
          .custom-table td {
            border: 1px solid #666 !important;
            padding: 4px 4px !important;
            color: #000 !important;
            background: #fff !important;
            vertical-align: top !important;
          }

          .custom-table th {
            font-weight: 700 !important;
          }

          .custom-table th:nth-child(1),
          .custom-table td:nth-child(1) {
            width: 9% !important;
            white-space: nowrap !important;
          }

          .custom-table th:nth-child(2),
          .custom-table td:nth-child(2) {
            width: 35% !important;
            font-size: 7.5px !important;
            line-height: 1.12 !important;
            word-break: break-word !important;
          }

          .custom-table th:nth-child(3),
          .custom-table td:nth-child(3) {
            width: 5% !important;
            text-align: center !important;
            white-space: nowrap !important;
          }

          .custom-table th:nth-child(4),
          .custom-table td:nth-child(4) {
            width: 6% !important;
            text-align: right !important;
            white-space: nowrap !important;
          }

          .custom-table th:nth-child(5),
          .custom-table td:nth-child(5) {
            width: 10% !important;
            text-align: right !important;
            white-space: nowrap !important;
          }

          .custom-table th:nth-child(6),
          .custom-table td:nth-child(6) {
            width: 12% !important;
            text-align: right !important;
            white-space: nowrap !important;
          }

          .custom-table th:nth-child(7),
          .custom-table td:nth-child(7) {
            width: 11% !important;
            text-align: right !important;
            white-space: nowrap !important;
          }

          .custom-table th:nth-child(8),
          .custom-table td:nth-child(8) {
            width: 12% !important;
            text-align: right !important;
            white-space: nowrap !important;
          }
        }

        @media screen {
          .print-only {
            display: none !important;
          }

          .lezaky-num {
            text-align: right;
            white-space: nowrap;
          }

          .lezaky-center {
            text-align: center;
            white-space: nowrap;
          }

          .lezaky-name {
            min-width: 320px;
          }
        }
      `}</style>

      <div className="flex flex-col gap-3 mb-4 no-print">
        <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-3">
          <div className="flex items-center gap-4 flex-wrap">
            <h2 className="text-2xl font-semibold">Ležáky</h2>

            {lastUpdated ? (
              <span className="text-sm text-gray-500">
                Poslední export:{" "}
                {new Date(lastUpdated)
                  .toLocaleString("cs-CZ", {
                    day: "numeric",
                    month: "numeric",
                    year: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                    second: "2-digit",
                  })
                  .replace(/\. /g, ".")}
              </span>
            ) : !loading ? (
              <span className="text-sm text-rose-600">
                Zatím nebyl nahrán žádný export
              </span>
            ) : null}
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            <button
              type="button"
              onClick={() => setShowAdvancedFilters((prev) => !prev)}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border bg-white hover:bg-gray-50 text-sm"
            >
              <SlidersHorizontal className="w-4 h-4" />
              {showAdvancedFilters ? "Skrýt filtry" : "Další filtry"}
            </button>

            <button
              type="button"
              onClick={clearFilters}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border bg-white hover:bg-gray-50 text-sm"
            >
              <X className="w-4 h-4" />
              Vymazat
            </button>

            <button
              type="button"
              onClick={handlePrint}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 text-sm"
            >
              <Printer className="w-4 h-4" />
              Tisk
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[minmax(260px,1fr)_220px_auto] gap-3 items-end">
          <div className="border rounded-xl p-3 bg-white shadow-sm">
            <div className="text-xs font-semibold text-gray-600 mb-2">
              HLEDAT
            </div>
            <input
              type="text"
              value={textFilters.search}
              onChange={(e) => handleTextFilterChange("search", e.target.value)}
              placeholder="Název / PDK"
              className="border p-2 rounded w-full"
            />
          </div>

          <div className="border rounded-xl p-3 bg-white shadow-sm">
            <div className="text-xs font-semibold text-gray-600 mb-2">
              SKUPINA VP
            </div>
            <select
              value={textFilters.skupinaVp}
              onChange={(e) => handleTextFilterChange("skupinaVp", e.target.value)}
              className="border p-2 rounded w-full"
            >
              <option value="">Vše</option>
              <option value="__empty__">Prázdné</option>
              {SKUPINA_VP_OPTIONS.map((value) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
            </select>
          </div>

          <div className="text-sm text-gray-500 pb-3">
            Zobrazeno záznamů: {filteredAndSortedData.length} / {data.length}
          </div>
        </div>

        {showAdvancedFilters && (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-3">
            {renderRangeFilter("mnozstvi", "MNOŽSTVÍ", "number")}
            {renderRangeFilter("ncsdKs", "NCSD/KS", "number")}
            {renderRangeFilter("ncsdSum", "NCSD CELKEM", "number")}
            {renderRangeFilter("exspirace", "EXSPIRACE", "date")}
            {renderRangeFilter("zbyvaDni", "ZBÝVÁ DNÍ", "number")}
          </div>
        )}
      </div>

      <div className="print-only mb-4">
        <h1 className="text-xl font-bold">Ležáky</h1>
        <div className="text-sm mt-1">
          Pobočka: {String(user?.pharmacyCode || "").replace(/^0+/, "") || "-"}
        </div>
        <div className="text-sm">
          Vytištěno:{" "}
          {new Date().toLocaleString("cs-CZ", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
          })}
        </div>
        {lastUpdated ? (
          <div className="text-sm">
            Poslední export:{" "}
            {new Date(lastUpdated)
              .toLocaleString("cs-CZ", {
                day: "2-digit",
                month: "2-digit",
                year: "numeric",
                hour: "2-digit",
                minute: "2-digit",
              })
              .replace(/\. /g, ".")}
          </div>
        ) : null}
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-gray-500">
          <Loader2 className="w-4 h-4 animate-spin" />
          Načítám data...
        </div>
      ) : filteredAndSortedData.length === 0 ? (
        <p>Nenalezeny žádné ležáky.</p>
      ) : (
        <div className="print-table-wrap overflow-x-auto">
          <Table className="custom-table">
            <thead>
              <tr>
                <th onClick={() => handleSort("pdk")} className="cursor-pointer select-none">
                  PDK{renderSortMark("pdk")}
                </th>
                <th onClick={() => handleSort("nazev")} className="cursor-pointer select-none">
                  NÁZEV{renderSortMark("nazev")}
                </th>
                <th onClick={() => handleSort("skupinaVp")} className="cursor-pointer select-none">
                  VP{renderSortMark("skupinaVp")}
                </th>
                <th onClick={() => handleSort("mnozstvi")} className="cursor-pointer select-none">
                  KS{renderSortMark("mnozstvi")}
                </th>
                <th onClick={() => handleSort("ncsdKs")} className="cursor-pointer select-none">
                  NCSD/KS{renderSortMark("ncsdKs")}
                </th>
                <th onClick={() => handleSort("ncsdSum")} className="cursor-pointer select-none">
                  NCSD CELKEM{renderSortMark("ncsdSum")}
                </th>
                <th onClick={() => handleSort("exspirace")} className="cursor-pointer select-none">
                  EXPIRACE{renderSortMark("exspirace")}
                </th>
                <th onClick={() => handleSort("zbyvaDni")} className="cursor-pointer select-none">
                  ZBÝVÁ DNÍ{renderSortMark("zbyvaDni")}
                </th>
              </tr>
            </thead>

            <tbody>
              {filteredAndSortedData.map((item, index) => (
                <tr key={`${item.pdk || item.kodVZP || item.nazev || "row"}-${index}`}>
                  <td>{item.pdk || ""}</td>
                  <td className="lezaky-name">{item.nazev || ""}</td>
                  <td className="lezaky-center">{item.skupinaVp || ""}</td>
                  <td className="lezaky-num">{formatPlainNumber(item.mnozstvi)}</td>
                  <td className="lezaky-num">{formatMoney(item.ncsdKs)}</td>
                  <td className="lezaky-num">{formatMoney(item.ncsdSum)}</td>
                  <td className="lezaky-num">{item.exspirace || ""}</td>
                  <td className="lezaky-num">{item.zbyvaDni ?? ""}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      )}
    </div>
  );
}